import React from "react";
import moment from "moment";
import I18n from "i18n-js";

import ProgressBar from "../../commons/ProgressBar";

import Card from "./Card";
import GaugeGraph from "./GaugeGraph";

import pdpIcon from "../../assets/icons/navigation/pdp.svg";
import developmentPlanIcon from "../../assets/icons/dashboard/development-plan.svg";

const getStyles = (expectedProgress) => ([
  {
    name: "poor",
    min: 0.0,
    max: (expectedProgress * 0.8) - 0.001,
    data: {
      color: "#D22F3A",
    }
  },
  {
    name: "good",
    min: expectedProgress * 0.8,
    max: expectedProgress,
    data: {
      color: "#FABF38"
    }
  },
  {
    name: "excellent",
    min: expectedProgress + 0.001,
    max: 1.0,
    data: {
      color: "#78C0B6"
    }
  }
]);

const formatDate = (date) => {
  const offsetDate = moment.utc(date).local().toDate();
  return I18n.l("date.formats.default", offsetDate);
}

const DevelopmentPlan = ({ plan, ofuscate }) => {

  const expectedProgress = Math.round(plan?.expected_progress * 100.0);
  const expectedProgressColor = plan?.expected_progress >= 1.0 ? "#E80000" : null;

  const objectiveCount = plan?.objective_count;
  const completedObjectiveCount = plan?.completed_objective_count;
  const completedObjectiveProgress = Math.round((completedObjectiveCount / objectiveCount) * 100.0);
  
  const taskCount = plan?.task_count;
  const completedTaskCount = plan?.completed_task_count;
  const completedTaskProgress = Math.round((completedTaskCount / taskCount) * 100.0);

  const renderPlanStatus = () => {
    const planIsLate = expectedProgress > plan?.progress;
    const status = plan?.due ? "due" : planIsLate ? "late" : "ok";

    return (
      <div className={`status ${status}`}>
        {I18n.t(`dashboard.development_plan.status.${status}`)}
      </div>
    );
  }

  const renderContent = () => {

    if (!Boolean(plan)) {
      return (
        <span className="no-plans">
          {I18n.t("dashboard.development_plan.no_development_plan")}
        </span>
      );
    }

    return (
      <div className="development-plan-content">
        <div className="graph">
          <GaugeGraph
            title={I18n.t("dashboard.development_plan.title")}
            icon={developmentPlanIcon}
            score={plan?.progress}
            ofuscate={ofuscate}
            ofuscateTitle={ofuscate}
            styles={getStyles(plan?.expected_progress)}
          />
          {renderPlanStatus()}
        </div>
        <div className="stats">
          <div className="stat">
            <div className="title">
              <span className="label">{I18n.t("dashboard.development_plan.target_date")}</span>
              <span className="value">{formatDate(plan?.target_date)}</span>
            </div>
            <ProgressBar value={expectedProgress} color={expectedProgressColor} />
          </div>
          <div className="stat">
            <div className="title">
              <span className="label">{I18n.t("dashboard.development_plan.objectives")}</span>
              <span className="value">{`${completedObjectiveCount}/${objectiveCount}`}</span>
            </div>
            <ProgressBar value={completedObjectiveProgress} />
          </div>
          <div className="stat">
            <div className="title">
              <span className="label">{I18n.t("dashboard.development_plan.tasks")}</span>
              <span className="value">{`${completedTaskCount}/${taskCount}`}</span>
            </div>
            <ProgressBar value={completedTaskProgress} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="development-plan">
      <Card
        icon={pdpIcon}
        title={I18n.t("dashboard.development_plan.title")}
        updateLink="/pdp"
        ofuscate={ofuscate}
        content={renderContent()}
      />
    </div>
  );
}

export default DevelopmentPlan;
